import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ShoppingBag, DollarSign, Users, Clock, ArrowUpRight, TrendingUp } from "lucide-react";
import { apiAdminStats, apiAdminRevenueTrend } from "../lib/api";
import RevenueChart from "./RevenueChart";

const statusColor = {
  pending: "text-amber-700 bg-amber-100",
  paid: "text-blue-700 bg-blue-100",
  shipped: "text-indigo-700 bg-indigo-100",
  delivered: "text-green-700 bg-green-100",
  cancelled: "text-gray-700 bg-gray-100",
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => { apiAdminStats().then(setStats); }, []);
  useEffect(() => { apiAdminRevenueTrend(days).then(setTrend); }, [days]);

  if (!stats) return <div className="p-10 text-sm text-gray-500">Loading…</div>;

  const cards = [
    { icon: DollarSign, label: "Revenue", value: `$${stats.revenue.toFixed(2)}`, link: "/admin/orders" },
    { icon: ShoppingBag, label: "Total Orders", value: stats.totalOrders, link: "/admin/orders" },
    { icon: Clock, label: "Pending", value: stats.pendingOrders, link: "/admin/orders" },
    { icon: Package, label: "Products", value: stats.totalProducts, link: "/admin/products" },
    { icon: Users, label: "Subscribers", value: stats.totalSubscribers, link: "/admin/subscribers" },
  ];

  return (
    <div className="p-10">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-px w-8 bg-[#b91c1c]" />
        <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">Overview</span>
      </div>
      <h1 className="font-display text-4xl font-semibold text-[#0a0a0a] tracking-tight">Dashboard</h1>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-gray-200">
        {cards.map((c) => (
          <Link key={c.label} to={c.link} className="bg-white p-6 hover:bg-[#fafbfc] transition-colors">
            <c.icon className="h-4 w-4 text-[#b91c1c] mb-3" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">{c.label}</div>
            <div className="font-display text-3xl font-semibold text-[#0a0a0a]">{c.value}</div>
          </Link>
        ))}
      </div>

      <div className="mt-10 bg-white border border-gray-200 p-6" data-testid="dashboard-revenue-chart-card">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 text-[#b91c1c] mb-1">
              <TrendingUp className="h-4 w-4" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.32em]">Revenue Trend</span>
            </div>
            <h2 className="font-display text-xl font-semibold text-[#0a0a0a]">Last {days} days</h2>
          </div>
          <div className="flex gap-1">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 h-8 text-[11px] font-semibold uppercase tracking-[0.18em] border ${
                  days === d ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "bg-white text-gray-600 border-gray-300 hover:border-[#0a0a0a]"
                }`}
                data-testid={`chart-range-${d}`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
        <RevenueChart series={trend?.series || []} />
      </div>

      <div className="mt-10">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-semibold text-[#0a0a0a]">Recent orders</h2>
          <Link to="/admin/orders" className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0a0a0a] hover:text-[#b91c1c] flex items-center gap-1">
            View all <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="bg-white border border-gray-200">
          {(stats.recentOrders || []).length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">No orders yet</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#fafbfc] border-b border-gray-200 text-[10px] uppercase tracking-[0.18em] text-gray-500">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Order</th>
                  <th className="text-left px-5 py-3 font-semibold">Customer</th>
                  <th className="text-left px-5 py-3 font-semibold">Status</th>
                  <th className="text-right px-5 py-3 font-semibold">Total</th>
                  <th className="text-right px-5 py-3 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentOrders.map((o) => (
                  <tr key={o.orderNumber} className="border-b border-gray-100">
                    <td className="px-5 py-3 font-semibold text-[#0a0a0a]">{o.orderNumber}</td>
                    <td className="px-5 py-3 text-gray-700">{o.customer}</td>
                    <td className="px-5 py-3"><span className={`text-[10px] font-semibold uppercase tracking-[0.18em] px-2 py-1 ${statusColor[o.status] || "bg-gray-100"}`}>{o.status}</span></td>
                    <td className="px-5 py-3 text-right font-semibold">${o.total.toFixed(2)}</td>
                    <td className="px-5 py-3 text-right text-gray-500 text-xs">{new Date(o.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
